import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Card,
    Table,
    DatePicker,
    Input,
    Select,
    Button,
    Space,
    Row,
    Col,
    Alert,
    Dropdown,
    Typography,
    message,
    Modal,
    InputNumber,
    Checkbox
} from 'antd';
import {
    SearchOutlined,
    DownloadOutlined,
    DeleteOutlined,
    PrinterOutlined,
    FileExcelOutlined,
    BarChartOutlined,
    EyeOutlined,
    EditOutlined,
    FileTextOutlined,
    CalculatorOutlined,
    CheckOutlined,
    PlayCircleOutlined,
    CloseCircleOutlined,
    MoreOutlined,
    InfoCircleOutlined,
    ExportOutlined,
    ScissorOutlined,
    StopOutlined,
    PlusOutlined,
    ClockCircleOutlined,
    UploadOutlined,
    FilterOutlined,
    ClearOutlined,
    CaretUpOutlined,
    CaretDownOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { getNumericFilters, getStringFilters, getDateFilters, formatCellValue, isDateExpired, isDateApproaching, applyAdvancedFilter, getConfirmModalProps, redirectToPivotWithData, exportToExcel, printTable, ActionButtons } from '../../utils/tableFilters';
import { trIncludes } from '../../utils/trText';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import * as XLSX from 'xlsx';
import FormikForm from '../form/FormikForm';
import '../fatura/FaturaPage.css';
import { useNavigate } from 'react-router-dom';
import Chatbot from '../giris/Chatbot.jsx';
import { useAuth } from '../../context/AuthContext';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

const AraclarPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { darkMode } = useTheme();
    const [activeTab, setActiveTab] = useState('araclar-liste');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        dateRange: null,
        search: '',
        page: 1,
        pageSize: 50,
    });
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
        pageSize: 10
    });
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [currentFormId, setCurrentFormId] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const [formTableName, setFormTableName] = useState('');
    const [formUniqueField, setFormUniqueField] = useState(null);
    const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
    const [currentFormExtraData, setCurrentFormExtraData] = useState({});
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailData, setDetailData] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [currentRecordId, setCurrentRecordId] = useState(null);
    const [modalButtonType, setModalButtonType] = useState('main-table');
    const [vehicleFilter, setVehicleFilter] = useState('ALL');
    const [vehicleOptions, setVehicleOptions] = useState([])   
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [columns, setColumns] = useState([]);
    const [showData, setShowData] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [columnSelectDropdownOpen, setColumnSelectDropdownOpen] = useState(false);
    const loadDataRef = useRef(null);

    useEffect(() => {
        if (darkMode) {
            document.body.setAttribute('data-theme', 'dark');
        } else {
            document.body.removeAttribute('data-theme');
        }
    }, [darkMode]);

    const confirmDelete = (tableName, idField, record, successMessage) => {
        const id = record[idField] ?? record.id;
        Modal.confirm({
            ...getConfirmModalProps('Silme Onayı', 'Bu aracı silmek istediğinize emin misiniz?', darkMode),
            onOk: async () => {
                try {
                    const res = await fetch(`${API_BASE_URL}/list/generic/update-active`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ table_name: tableName, id, active: 0 }),
                    });
                    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                    message.success(successMessage);
                    loadDataRef.current?.();
                } catch (err) {
                    console.error('Silme işlemi sırasında hata:', err);
                    message.error('Silme işlemi başarısız: ' + err.message);
                }
            },
        });
    };

    const openFormForRecord = (record, idField, formId, title, tableName, extraData = null) => {
        const id = record[idField];
        if (id) {
            handleOpenForm(formId, title, tableName, 'id', id, extraData);
        } else {
            message.error('ID bulunamadı');
        }
    };

    const aracTabs = [
        {
            key: 'araclar-liste',
            label: 'Araçlar',
            sqlKey: 'araclar-liste',
            formManagerId: 28,
            actionButtons: {
                'KEY_ARACLAR': [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_araclar', 131, 'Araç Özellikleri', 'araclar')
                    },
                    {
                        key: 'delete',
                        label: 'Sil',
                        type: 'default',
                        icon: 'DeleteOutlined',
                        onClick: (record) => confirmDelete('araclar', 'key_araclar', record, 'Araç başarıyla silindi')
                    },
                    {
                        key: 'view',
                        label: 'Detay',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => navigate(`/arac-detay/araclar/${record.key_araclar}`)
                    },
                    {
                        key: 'upload',
                        label: 'Yükle',
                        type: 'default',
                        icon: 'PlusOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_araclar', 109, 'Araç Ruhsat Dosya Ekle', 'araclar_dosya', 
                            { 
                                ekleyen: user?.kullanici_no || null,
                                root: `arac-ruhsat`,
                                dir: 'arac-ruhsat', 
                            })
                    },
                    {
                        key: 'viewFile',
                        label: 'Dosya Aç',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => handleOpenDetailModal(record, null, 'sub-table', 'arac-ruhsat')
                    }
                ],
                'KEY_DEMIRBAS_ZIMMET': [
                    {
                        key: 'viewZimmet',
                        label: 'Zimmet',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => {
                            handleOpenDetailModal(record, null, 'main-table');
                        }
                    }
                ]
            }
        },
        {
            key: 'arac-kaza-liste',
            label: 'Hasarlar',
            sqlKey: 'arac-kaza-liste',
            formManagerId: 31,
            actionButtons: {
                'KEY_KAZA_LISTE': [
                    {
                        key: 'upload',
                        label: 'Yükle',
                        type: 'default',
                        icon: 'UploadOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_kaza_liste', 115, 'Araç Kaza Dosya', 'araclar_dosya')
                    },
                    {
                        key: 'viewFile',
                        label: 'Dosya Aç',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => handleOpenDetailModal(record, null, 'sub-table', 'arac-kaza')
                    }
                ]
            }
        },
        {
            key: 'arac-lastik-liste',
            label: 'Lastikler',
            sqlKey: 'arac-lastik-liste',
            formManagerId: 32,
            actionButtons: {
                'KEY_LASTIK_LISTE': [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_lastik_liste', 132, 'Araç Lastik', 'arac_lastik')
                    }
                ]
            }
        },
        {
            key: 'arac-ceza-liste',
            label: 'Cezalar',
            sqlKey: 'arac-ceza-liste',
            formManagerId: 33,
            actionButtons: {
                'KEY_CEZA_LISTE': [
                    {
                        key: 'upload',
                        label: 'Yükle',
                        type: 'default',
                        icon: 'UploadOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_ceza_liste', 117, 'Araç Ceza Dosya', 'araclar_dosya')
                    },
                    {
                        key: 'view',
                        label: 'Görüntüle',
                        type: 'default',
                        icon: 'UploadOutlined',
                        onClick: (record) => handleOpenDetailModal(record, null, 'sub-table', 'arac-ceza')
                    }
                ]
            }
        },
        {
            key: 'arac-ariza-liste',
            label: 'Arızalar',
            sqlKey: 'arac-ariza-liste',
            formManagerId: 34,
            actionButtons: {
                'KEY_ARIZA_LISTE': [
                    {
                        key: 'upload',
                        label: 'Yükle',
                        type: 'default',
                        icon: 'UploadOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_ariza_liste', 119, 'Araç Arıza Dosya', 'araclar_dosya')
                    },
                    {
                        key: 'view',
                        label: 'Dosya Aç',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => handleOpenDetailModal(record, null, 'sub-table', 'arac-ariza')
                    }
                ]
            }
        },
        {
            key: 'arac-sigorta-liste',
            label: 'Sigortalar',
            sqlKey: 'arac-sigorta-liste',
            formManagerId: 35,
            actionButtons: {
                'KEY_SIGORTA_LISTE': [
                    {
                        key: 'upload',
                        label: 'Yükle',
                        type: 'default',
                        icon: 'UploadOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_sigorta_liste', 117, 'Araç Sigorta Listesi', 'araclar_dosya')
                    },
                    {
                        key: 'view',
                        label: 'Dosya Aç',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => handleOpenDetailModal(record, null, 'sub-table', 'arac-sigorta')
                    }
                ]
            }
        },
        {
            key: 'arac-muayene-liste',
            label: 'Muayeneler',
            sqlKey: 'arac-muayene-liste',
            formManagerId: 38,
            actionButtons: {
                'KEY_MUAYENE_LISTE': [
                    {
                        key: 'upload',
                        label: 'Yükle',
                        type: 'default',
                        icon: 'PlusOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_muayene_liste', 113, 'Araç Muayene Dosya', 'araclar_dosya')
                    },
                    {
                        key: 'view',
                        label: 'Dosya Aç',
                        type: 'default',
                        icon: 'EyeOutlined',
                        condition: (record) => record.file == null || record.file == '',
                        onClick: (record) => handleOpenDetailModal(record, null, 'sub-table', 'arac-muayene')
                    }
                ]
            }
        },
        {
            key: 'arac-bakim-liste',
            label: 'Bakımlar',
            sqlKey: 'arac-bakim-liste',
            formManagerId: 39,
            actionButtons: {
                'KEY_BAKIM_LISTE': [
                    {
                        key: 'upload',
                        label: 'Yükle',
                        type: 'default',
                        icon: 'PlusOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_bakim_liste', 111, 'Araç Bakım Dosya', 'araclar_dosya')
                    },
                    {
                        key: 'view',
                        label: 'Dosya Aç',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => handleOpenDetailModal(record, null, 'sub-table', 'arac-bakim')
                    }
                ]
            }
        },
        {
            key: 'arac-zimmet-liste',
            label: 'Araç Zimmetleri',
            sqlKey: 'arac-zimmet-liste',
            formManagerId: 30,
            actionButtons: {
                'KEY_ZIMMET': [
                    {
                        key: 'add',
                        label: 'Teslimat Ekle',
                        type: 'default',
                        icon: 'PlusOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_zimmet', 100, 'Zimmet Teslim', 'zimmet')
                    },
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        icon: 'EditOutlined',
                        onClick: (record) => openFormForRecord(record, 'key_zimmet', 98, 'Zimmet', 'zimmet')
                    },
                    {
                        key: 'view',
                        label: 'Tutanak',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => {
                            console.log("Yönlendirme yapılmalı");
                        }
                    }
                ],
                'KEY_DEMIRBAS_ZIMMET': [
                    {
                        key: 'view',
                        label: 'Zimmet',
                        type: 'default',
                        icon: 'EyeOutlined',
                        onClick: (record) => {
                            handleOpenDetailModal(record, null, 'main-table');
                        }
                    }
                ]
            }
        },
        {
            key: 'arac-sozlesme-liste',
            label: 'Sözleşmeler',
            sqlKey: 'arac-sozlesme-liste',
            formManagerId: 36,
            actionButtons: {
                'KEY_ARACSOZLESME_LISTE': [
                    {
                        key: 'edit',
                        label: 'Düzenle',
                        type: 'default',
                        onClick: (record) => openFormForRecord(record, 'key_aracsozlesme_liste', 146, 'Araç Sözleşmeleri', 'arac_sozlesme')
                    },
                    {
                        key: 'upload',
                        label: 'Yükle',
                        type: 'default',
                        onClick: (record) => openFormForRecord(record, 'key_aracsozlesme_liste', 125, 'Araç Sözleşme Dosya', 'arac_sozlesme')
                    },
                    {
                        key: 'view',
                        label: 'Dosya Aç',
                        type: 'default',
                        onClick: (record) => handleOpenDetailModal(record, null, 'sub-table', 'arac-sozlesme')
                    }
                ]
            }
        },
        {
            key: 'arac-yolculuklar-liste',
            label: 'Seyahatler',
            sqlKey: 'arac-yolculuklar-ozet',
            dateRange: {
                startOffset: -1,
                endOffset: 0,
                unit: 'month',
                noDefaultDate: false
            },
            noDateFilter: false,
            hasVehicleFilter: true
        }
    ];

    const calculateDateRange = (tabData) => {
        if (!tabData.dateRange) return null;
        const { startOffset, endOffset, unit } = tabData.dateRange;
        const today = dayjs();
        const startDate = today.add(startOffset, unit);
        const endDate = today.add(endOffset, unit);
        return [startDate, endDate];
    };

    const loadVehicleOptions = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/list/execute-query/arac-plaka-listesi`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ params: {} })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const vehicleData = await response.json();
            setVehicleOptions(vehicleData || []);
        } catch (error) {
            console.error('Error loading vehicle options:', error);
            setVehicleOptions([]);
        }
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const activeTabData = aracTabs.find(tab => tab.key === activeTab);
            if (!activeTabData) {
                console.warn('Active tab data not found for key:', activeTab);
                return;
            }

            let url, response, allData;
            if (activeTab === 'arac-yolculuklar-liste') {
                const effectiveDateRange = filters.dateRange || calculateDateRange(activeTabData);
                const startDate = effectiveDateRange ? effectiveDateRange[0].format('YYYY-MM-DD HH:mm') : dayjs().subtract(1, 'month').format('YYYY-MM-DD HH:mm');
                const endDate = effectiveDateRange ? effectiveDateRange[1].format('YYYY-MM-DD HH:mm') : dayjs().format('YYYY-MM-DD HH:mm');

                const params = {
                    ilkTar: startDate,
                    sonTar: endDate,
                    plaka: vehicleFilter
                };

                response = await fetch(`${API_BASE_URL}/list/execute-query/arac-yolculuklar-ozet`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ params })
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error(`API Error - Status: ${response.status}, Response:`, errorText);
                    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
                }

                allData = await response.json();
            } else {
                const urlParams = new URLSearchParams();
                url = `${API_BASE_URL}/list/execute/${activeTabData.sqlKey}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
                response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error(`API Error - Status: ${response.status}, Response:`, errorText);
                    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
                }

                allData = await response.json();
            }

            if (!Array.isArray(allData)) {
                console.warn('API response is not an array:', allData);
                setError('Geçersiz veri formatı - API array döndürmüyor');
                return;
            }

            let filteredData = [...allData];
            const shouldApplyDateFilter = !activeTabData.noDateFilter;
            const dynamicDateRange = shouldApplyDateFilter &&
                !(activeTabData.dateRange && activeTabData.dateRange.noDefaultDate)
                ? calculateDateRange(activeTabData) : null;
            const effectiveDateRange = filters.dateRange || dynamicDateRange;
            
            if (activeTab !== 'arac-yolculuklar-liste' && shouldApplyDateFilter && effectiveDateRange && effectiveDateRange[0] && effectiveDateRange[1]) {
                const filterStartDate = effectiveDateRange[0].startOf('day');
                const filterEndDate = effectiveDateRange[1].endOf('day');
                filteredData = filteredData.filter(record => {
                    const satinAlinmaTarihi = record['Satın Alınma Tarihi'];
                    if (!satinAlinmaTarihi) {
                        return false;
                    }
                    let recordDate;
                    const dateValue = String(satinAlinmaTarihi).trim();

                    if (dateValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                        recordDate = dayjs(dateValue, 'DD.MM.YYYY');
                    } else if (dateValue.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
                        recordDate = dayjs(dateValue, 'YYYY.MM.DD');
                    } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        recordDate = dayjs(dateValue, 'YYYY-MM-DD');
                    } else {
                        recordDate = dayjs(satinAlinmaTarihi);
                    }

                    if (recordDate.isValid()) {
                        return recordDate.isBetween(filterStartDate, filterEndDate, null, '[]');
                    }
                    return false;
                });
            }

            if (filters.search && filters.search.trim() !== '') {
                const searchTerm = filters.search.trim();
                filteredData = filteredData.filter(record => {
                    return Object.values(record).some(value => {
                        if (value === null || value === undefined) return false;
                        return trIncludes(String(value), searchTerm);
                    });
                });
            }

            if (filteredData.length > 0) {
                const sample = filteredData[0];
                const dynamicCols = Object.keys(sample)
                    .filter((key) => !key.toLowerCase().includes('_row_style'))
                    .map((key) => {
                        const firstValue = sample[key];
                        const isDateColumn = key.toLowerCase().includes('tarih') || key.toLowerCase().includes('tar');
                        const isNumber = typeof firstValue === "number" && !isNaN(firstValue);
                        const uniqueValues = [...new Set(filteredData.map((item) => item[key]))].filter(
                            (val) => val !== undefined && val !== null && val !== ""
                        );

                        let filters = [];
                        if (isNumber) {
                            filters = getNumericFilters();
                        } else if (isDateColumn) {
                            filters = getDateFilters();
                        } else {
                            filters = getStringFilters();
                        }

                        return {
                            key: key,
                            title: key,
                            dataIndex: key,
                            isDateColumn,
                            isNumber,
                            isString: !isNumber && !isDateColumn,
                            uniqueValues,
                            sortable: true,
                            filters, 
                        };
                    });
                setColumns(dynamicCols);
            }

            setData(filteredData || []);
            setShowData(filteredData || []);
            setPagination(prev => ({
                ...prev,
                total: filteredData?.length || 0
            }));
        } catch (err) {
            console.error('Data load error:', err);
            setError('Veri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [activeTab, filters.dateRange, filters.search, vehicleFilter]);

    useEffect(() => {
        loadDataRef.current = loadData;
    }, [loadData]);

    const handlePivotRedirect = useCallback(() => {
        const activeTabData = aracTabs.find(tab => tab.key === activeTab);
        if (!activeTabData?.sqlKey) return;
        redirectToPivotWithData({
            data,
            filters,
            tabLabel: activeTabData.label || 'Araçlar',
            sqlKey: activeTabData.sqlKey,
            navigate,
            message,
            setLoading,
            apiBaseUrl: API_BASE_URL,
        });
    }, [activeTab, data, filters, navigate]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        const newTabData = aracTabs.find(tab => tab.key === key);
        const shouldSetDefaultDate = newTabData &&
            !newTabData.noDateFilter &&
            !(newTabData.dateRange && newTabData.dateRange.noDefaultDate);
        const dynamicDateRange = shouldSetDefaultDate ? calculateDateRange(newTabData) : null;
        const defaultYear = newTabData && newTabData.dateType === 'yeartime' ?
            (newTabData.defaultYear || new Date().getFullYear()) : null;
        const defaultMonth = newTabData && newTabData.dateType === 'yeartime' ?
            (newTabData.defaultMonth || new Date().getMonth() + 1) : null;
        setFilters({
            dateRange: dynamicDateRange,
            search: '',
            durum: null,
            firma: null,
            page: 1,
            pageSize: 25,
            year: defaultYear,
            month: defaultMonth
        });
        setPagination({
            current: 1,
            total: 0,
            pageSize: 10
        });
    };

    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize || prev.pageSize
        }));
    };

    const handleAdvancedFilterChange = (columnKey, valueType, value) => {
        setFilteredInfo((prev) => ({
            ...prev,
            [columnKey]: {
                ...prev[columnKey],
                [valueType]: value,
            },
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const clearColumnFilter = (columnKey) => {
        setFilteredInfo((prev) => {
            const newFilteredInfo = { ...prev };
            delete newFilteredInfo[columnKey];
            return newFilteredInfo;
        });
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const clearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setFilters({
            dateRange: null,
            search: '',
            page: 1,
            pageSize: 50
        });
        setSortedInfo({});
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    }, []);

    const handleSort = (columnKey) => {
        const column = columns.find((col) => col.key === columnKey);
        if (!column) return;

        let newOrder = "asc";
        if (sortedInfo.columnKey === columnKey) {
            if (sortedInfo.order === "asc") {
                newOrder = "desc";
            } else if (sortedInfo.order === "desc") {
                newOrder = null;
            }
        }

        setSortedInfo({
            columnKey: newOrder ? columnKey : null,
            order: newOrder,
        });
    };

    const handleOpenForm = (formId, title = 'Form', tableName = '', uniqueField = '', uniqueValue = null, extraData = null) => {
        setModalTitle(title);
        setCurrentFormId(formId);
        setFormTableName(tableName);
        setFormUniqueField(uniqueField);
        setCurrentUniqueValue(uniqueValue);
        setCurrentFormExtraData(extraData || {});
        setFormModalOpen(true);
    };

    const handleFormSuccess = () => {
        setFormModalOpen(false);
        setCurrentFormId(null);
        setFormTableName('');
        setFormUniqueField('');
        setCurrentFormExtraData({});
        loadData();
    };

    const closeFormModal = () => {
        setFormModalOpen(false);
    };

    const handleOpenDetailModal = async (record, firstChar = null, modalType = 'main-table', fileType = null) => {
        const id = record.key_demirbas_zimmet || record.id || record.No || record.key_aracsozlesme_liste ||record.key_kaza_liste || record.key_ceza_liste || record.key_sigorta_liste || record.key_muayene_liste || record.key_bakim_liste || record.key_sigorta_liste || record.key_ariza_liste || record.key_ceza_liste || record.key_kaza_liste || record.key_muayene_liste || record.key_bakim_liste || record.key_sigorta_liste;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }

        const idString = String(id);
        setCurrentRecordId(idString);
        setDetailModalOpen(true);
        setDetailLoading(true);
        setDetailError(null);
        setModalButtonType(modalType);

        const charToUse = firstChar || idString.charAt(0).toLowerCase();
        let queryName = 'demirbas';
        let modalTitle = 'Araç Detayları';

        if (modalType === 'sub-table') {
            if (fileType) {
                queryName = 'arac-dosyalar';
                const fileTypeLabels = {
                    'arac-ruhsat': 'Ruhsat',
                    'arac-ariza': 'Arıza',
                    'arac-bakim': 'Bakım',
                    'arac-sozlesme': 'Sözleşme',
                    'arac-ceza': 'Ceza',
                    'arac-kaza': 'Kaza',
                    'arac-lastik': 'Lastik',
                    'arac-muayene': 'Muayene',
                    'arac-sigorta': 'Sigorta',
                    'arac-tahsis': 'Tahsis',
                    'arac-vergi': 'Vergi',
                    'arac-yedek': 'Yedek',
                    'arac-aksesuar': 'Aksesuar'
                };
                modalTitle = `${fileTypeLabels[fileType] || fileType} Dosyaları`;
            } else {
                queryName = 'demirbas';
                modalTitle = 'Araç Detayları';
            }
        } else {
            switch (charToUse) {
                case 'k':
                    modalTitle = 'Kredi Kartı Zimmet Detayları';
                    queryName = 'zimmet-detail-k';
                    break;
                case 'a':
                    modalTitle = 'Araç Zimmet Detayları';
                    queryName = 'zimmet-detail-a';
                    break;
                case 'd':
                    modalTitle = 'Araç Zimmet Detayları';
                    queryName = 'zimmet-detail-d';
                    break;
                default:
                    queryName = 'demirbas';
                    modalTitle = 'Araç Detayları';
                    break;
            }
        }

        let paramId = idString;
        let requestParams = { id: paramId };

        if (modalType === 'sub-table' && queryName === 'demirbas') {
            paramId = record.demirbas_id || record.id || idString;
            requestParams = { id: paramId };
        } else if (modalType === 'sub-table' && fileType) {
            const cleanId = idString.replace(/^[akd]/, '');
            requestParams = { id: cleanId, dosya_tipi: fileType };
        }

        try {
            const response = await fetch(`${API_BASE_URL}/list/execute-query/${queryName}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ params: requestParams })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setDetailData(Array.isArray(data) ? data : []);
            setModalTitle(modalTitle);
        } catch (err) {
            console.error('Detail modal data load error:', err);
            setDetailError('Detay verileri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetailModal = () => {
        setDetailModalOpen(false);
        setDetailData([]);
        setDetailError(null);
        setCurrentRecordId(null);
        setModalButtonType('main-table');
    };

    const handleViewFile = (record) => {
        const filePath = record.file;
        if (filePath) {
            const fileUrl = `https://dosya.atmturkiye.com/atmpoint/${filePath}`;
            window.open(fileUrl, '_blank');
        } else {
            message.warning('Dosya bulunamadı');
        }
    };

    const handleEditFile = (record) => {
        const id = record.key_zimmet_edit;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }
        handleOpenForm(98, 'Demirbaş Dosya Düzenle', 'zimmet', 'id', id);
    };

    const handleSubEditFile = (record) => {
        const id = record.dosyaID;
        if (!id) {
            message.error('Dosya ID bulunamadı');
            return;
        }
        const fileType = record.root;
        const formIds = {
            'arac-ruhsat': 110,
            'arac-kaza': 116, 
            'arac-bakim': 112,
            'arac-ceza': 118,
            'arac-ariza': 120,
            'arac-muayene': 108,
            'arac-sigorta': 114,
            'arac-sozlesme': 125,
        };

        const formId = formIds[fileType] || 109;
        const formTitle = `${fileType.replace('arac-', '').toUpperCase()} DOSYA DÜZENLE`;
        handleOpenForm(formId, formTitle, 'araclar_dosya', 'id', id);
    };

    const handleAddDelivery = (record) => {
        const id = record.key_zimmet_edit;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }
        handleOpenForm(100, 'Zimmet Teslim Formu', 'zimmet', 'id', id);
    };

    const filteredAndSortedData = useMemo(() => {
        let result = data.filter((item) => {
            const matchesSearch = Object.values(item).some((val) =>
                trIncludes(String(val), filters.search)
            );

            const matchesFilters = Object.entries(filteredInfo).every(([key, filter]) => {
                const recordValue = item[key];
                const col = columns.find((c) => c.key === key);
                return applyAdvancedFilter(recordValue, filter, col);
            });

            return matchesSearch && matchesFilters;
        });

        if (sortedInfo.columnKey && sortedInfo.order) {
            const column = columns.find((col) => col.key === sortedInfo.columnKey);
            if (column) {
                result.sort((a, b) => {
                    const valA = a[sortedInfo.columnKey];
                    const valB = b[sortedInfo.columnKey];

                    if (column.isDateColumn) {
                        let dateA = null, dateB = null;
                        
                        if (valA) {
                            const dateValueA = String(valA).trim();
                            if (dateValueA.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                dateA = dayjs(dateValueA, 'DD.MM.YYYY');
                            } else if (dateValueA.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                dateA = dayjs(dateValueA, 'YYYY-MM-DD');
                            } else {
                                dateA = dayjs(valA);
                            }
                        }
                        
                        if (valB) {
                            const dateValueB = String(valB).trim();
                            if (dateValueB.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                dateB = dayjs(dateValueB, 'DD.MM.YYYY');
                            } else if (dateValueB.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                dateB = dayjs(dateValueB, 'YYYY-MM-DD');
                            } else {
                                dateB = dayjs(valB);
                            }
                        }
                        
                        if (!dateA && !dateB) return 0;
                        if (!dateA || !dateA.isValid()) return sortedInfo.order === "asc" ? -1 : 1;
                        if (!dateB || !dateB.isValid()) return sortedInfo.order === "asc" ? 1 : -1;
                        const comparison = dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    } else if (column.isNumber) {
                        const numA = Number(valA) || 0;
                        const numB = Number(valB) || 0;
                        return sortedInfo.order === "asc" ? numA - numB : numB - numA;
                    } else {
                        const strA = String(valA || "").toLowerCase();
                        const strB = String(valB || "").toLowerCase();
                        const comparison = strA.localeCompare(strB, 'tr');
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    }
                });
            }
        }

        return result;
    }, [data, filters.search, filteredInfo, sortedInfo, columns]);

    const paginatedData = useMemo(() => {
        const startIndex = (pagination.current - 1) * pagination.pageSize;
        return filteredAndSortedData.slice(startIndex, startIndex + pagination.pageSize);
    }, [filteredAndSortedData, pagination.current, pagination.pageSize]);

    const saveSelectedColumnsToStorage = useCallback((columnKeys) => {
        try {
            const storageKey = `araclar_selected_columns_${activeTab}`;
            localStorage.setItem(storageKey, JSON.stringify(columnKeys));
        } catch (error) {
            console.error('Error saving selected columns to storage:', error);
        }
    }, [activeTab]);

    useEffect(() => {
        if (columns.length > 0) {
            try {
                const storageKey = `araclar_selected_columns_${activeTab}`;
                const saved = localStorage.getItem(storageKey);
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const validColumns = parsed.filter(key => columns.some(col => col.key === key));
                    if (validColumns.length > 0) {
                        setSelectedColumns(validColumns);
                    } else {
                        const allColumnKeys = columns.map(col => col.key);
                        setSelectedColumns(allColumnKeys);
                        saveSelectedColumnsToStorage(allColumnKeys);
                    }
                } else {
                    const allColumnKeys = columns.map(col => col.key);
                    setSelectedColumns(allColumnKeys);
                    saveSelectedColumnsToStorage(allColumnKeys);
                }
            } catch (error) {
                console.error('Error loading selected columns from storage:', error);
                const allColumnKeys = columns.map(col => col.key);
                setSelectedColumns(allColumnKeys);
            }
        }
    }, [columns, saveSelectedColumnsToStorage, activeTab]);

    const handleColumnSelectionChange = (checkedValues) => {
        setSelectedColumns(checkedValues);
        saveSelectedColumnsToStorage(checkedValues);
    };

    const handleSelectAllColumns = (checked) => {
        if (checked) {
            const allColumnKeys = columns.map(col => col.key);
            setSelectedColumns(allColumnKeys);
            saveSelectedColumnsToStorage(allColumnKeys);
        } else {
            setSelectedColumns([]);
            saveSelectedColumnsToStorage([]);
        }
    };

    const visibleColumns = useMemo(() => {
        if (selectedColumns.length === 0) {
            return columns;
        }
        return columns.filter(col => selectedColumns.includes(col.key));
    }, [columns, selectedColumns]);

    const removeColumn = (columnKey) => {
        setSelectedColumns((prev) => {
            const newSelected = prev.filter((key) => key !== columnKey);
            saveSelectedColumnsToStorage(newSelected);
            return newSelected;
        });
    };

    const getFilterDropdown = (column) => {
        if (!column) return null;
        const currentFilter = filteredInfo[column.key] || {};
        const condition = currentFilter.condition || "choose";

        return (
            <div style={{ padding: 16, minWidth: 300 }}>
                <Space direction="vertical" style={{ width: '100%', backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : 'var(--color-bg)', border: `1px solid ${darkMode ? 'var(--dark-border)' : 'var(--color-theme)'}`, padding: 12}}>
                    <div>
                        <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)', borderColor: darkMode ? 'var(--dark-border)' : 'var(--color-theme)' }}>
                            Filtre Şartı
                        </Text>
                        <Select
                            size="small"
                            value={condition}
                            onChange={(value) => handleAdvancedFilterChange(column.key, "condition", value)}
                            style={{ width: '100%' }}
                            className={darkMode ? 'dark-select' : ''}
                        >
                            {column.filters.map((filter) => (
                                <Option key={filter.value} value={filter.value}>
                                    {filter.text}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    {/* String değer seçimi */}
                    {column.isString && condition === "select" && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                Değer Seç
                            </Text>
                            <Select
                                size="small"
                                value={currentFilter.value || ""}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                                style={{ width: '100%' }}
                                placeholder="Seçiniz"
                                className={darkMode ? 'dark-select' : ''}
                            >
                                <Option value="">Seçiniz</Option>
                                {column.uniqueValues.map((val) => (
                                    <Option key={val} value={val}>
                                        {val}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    )}

                    {/* Sayısal filtreler için input alanları */}
                    {column.isNumber && condition !== "choose" && condition !== "between" && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                Değer
                            </Text>
                            <InputNumber
                                size="small"
                                placeholder="Değer giriniz"
                                value={currentFilter.value || ""}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    )}

                    {/* Sayısal arasında filtresi */}
                    {column.isNumber && condition === "between" && (
                        <>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                    Min Değer
                                </Text>
                                <InputNumber
                                    size="small"
                                    placeholder="Min"
                                    value={currentFilter.min || ""}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "min", value)}
                                    style={{ width: '100%' }}
                                    className={darkMode ? 'dark-input' : ''}
                                />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                    Max Değer
                                </Text>
                                <InputNumber
                                    size="small"
                                    placeholder="Max"
                                    value={currentFilter.max || ""}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "max", value)}
                                    style={{ width: '100%' }}
                                    className={darkMode ? 'dark-input' : ''}
                                />
                            </div>
                        </>
                    )}

                    {/* String filtreler için input alanları */}
                    {column.isString && !["choose", "empty", "notEmpty", "select"].includes(condition) && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                Değer
                            </Text>
                            <Input
                                size="small"
                                placeholder="Değer giriniz"
                                value={currentFilter.value || ""}
                                onChange={(e) => handleAdvancedFilterChange(column.key, "value", e.target.value)}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    )}

                    {/* Tarih filtreler için input alanları */}
                    {column.isDateColumn && ["equals", "before", "after"].includes(condition) && (
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                Tarih
                            </Text>
                            <DatePicker
                                size="small"
                                value={currentFilter.value ? dayjs(currentFilter.value, 'DD.MM.YYYY') : null}
                                onChange={(date) => handleAdvancedFilterChange(column.key, "value", date ? date.format('DD.MM.YYYY') : null)}
                                style={{ width: '100%' }}
                                format="DD.MM.YYYY"
                                className={darkMode ? 'dark-date-picker' : ''}
                            />
                        </div>
                    )}

                    {/* Tarih arasında filtresi */}
                    {column.isDateColumn && condition === "between" && (
                        <>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                    Başlangıç Tarihi
                                </Text>
                                <DatePicker
                                    size="small"
                                    value={currentFilter.startDate ? dayjs(currentFilter.startDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "startDate", date ? date.format('DD.MM.YYYY') : null)}
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                    className={darkMode ? 'dark-date-picker' : ''}
                                />
                            </div>
                            <div>
                                <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                    Bitiş Tarihi
                                </Text>
                                <DatePicker
                                    size="small"
                                    value={currentFilter.endDate ? dayjs(currentFilter.endDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "endDate", date ? date.format('DD.MM.YYYY') : null)}
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                    className={darkMode ? 'dark-date-picker' : ''}
                                />
                            </div>
                        </>
                    )}

                    <Button
                        size="small"
                        onClick={() => clearColumnFilter(column.key)}
                        style={{ marginTop: 8, width: '100%', borderColor: darkMode ? 'var(--dark-border)' : 'var(--color-theme)', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}
                        className={darkMode ? 'dark-button' : ''}
                    >
                        Filtreyi Sıfırla
                    </Button>
                </Space>
            </div>
        );
    };

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (activeTab === 'arac-yolculuklar-liste') {
            loadVehicleOptions();
        }
    }, [activeTab, loadVehicleOptions]);

    return (
        <div className={`fatura-page ${darkMode ? 'dark-theme' : ''}`}>
            {/* Filtreler */}
            <Card
                className={`fatura-filters-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Araç Türü
                                <EditOutlined onClick={() => {
                                    const activeTabData = aracTabs.find(tab => tab.key === activeTab);
                                    const formManagerId = activeTabData?.formManagerId;
                                    handleOpenForm(275, 'Liste Formu', 'sqls', 'id', formManagerId);
                                }} />
                            </Text>
                            <Select
                                placeholder="Araçlar İçin İşlem Seçin"
                                value={activeTab}
                                onChange={(value) => handleTabChange(value)}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-select' : ''}
                            >
                                {aracTabs.map(tab => (
                                    <Option key={tab.key} value={tab.key}>
                                        {tab.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </Col>
                    {/* Tarih Aralığı veya Yıl/Ay Seçici */}
                    {(() => {
                        const activeTabData = aracTabs.find(tab => tab.key === activeTab);
                        if (activeTabData && activeTabData.dateType === 'yeartime') {
                            return (
                                <>
                                    <Col xs={12} sm={6} md={3} lg={2}>
                                        <div className="filter-item">
                                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                                Yıl
                                            </Text>
                                            <Select
                                                value={filters.year}
                                                onChange={(value) => handleFilterChange('year', value)}
                                                style={{
                                                    width: '100%',
                                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                                className={darkMode ? 'dark-select' : ''}
                                            >
                                                {Array.from({ length: 10 }, (_, i) => {
                                                    const year = new Date().getFullYear() - 5 + i;
                                                    return (
                                                        <Option key={year} value={year}>{year}</Option>
                                                    );
                                                })}
                                            </Select>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={6} md={3} lg={2}>
                                        <div className="filter-item">
                                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                                Ay
                                            </Text>
                                            <Select
                                                value={filters.month}
                                                onChange={(value) => handleFilterChange('month', value)}
                                                style={{
                                                    width: '100%',
                                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                                className={darkMode ? 'dark-select' : ''}
                                            >
                                                {monthNames.map((month, index) => (
                                                    <Option key={index + 1} value={index + 1}>{month}</Option>
                                                ))}
                                            </Select>
                                        </div>
                                    </Col>
                                </>
                            );
                        } else {
                            return (
                                <Col xs={24} sm={12} md={6} lg={4}>
                                    <div className="filter-item">
                                        <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                            Tarih Aralığı
                                        </Text>
                                        <RangePicker
                                            value={filters.dateRange}
                                            onChange={(dates) => handleFilterChange('dateRange', dates)}
                                            style={{
                                                width: '100%',
                                                backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                                borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                color: darkMode ? 'var(--dark-text)' : undefined
                                            }}
                                            placeholder={['Başlangıç', 'Bitiş']}
                                            showTime={activeTab === 'arac-yolculuklar-liste' ? { format: 'HH:mm' } : false}
                                            format={activeTab === 'arac-yolculuklar-liste' ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY'}
                                            className={darkMode ? 'dark-date-picker' : ''}
                                            locale={{
                                                lang: {
                                                    locale: 'tr_TR',
                                                    placeholder: 'Tarih seçin',
                                                    rangePlaceholder: ['Başlangıç', 'Bitiş'],
                                                    today: 'Bugün',
                                                    now: 'Şimdi',
                                                    backToToday: 'Bugüne dön',
                                                    ok: 'Tamam',
                                                    clear: 'Temizle',
                                                    month: 'Ay',
                                                    year: 'Yıl',
                                                    timeSelect: 'Zaman seç',
                                                    dateSelect: 'Tarih seç',
                                                    monthSelect: 'Ay seç',
                                                    yearSelect: 'Yıl seç',
                                                    decadeSelect: 'On yıl seç',
                                                    yearFormat: 'YYYY',
                                                    dateFormat: 'DD.MM.YYYY',
                                                    dayFormat: 'DD',
                                                    dateTimeFormat: 'DD.MM.YYYY HH:mm:ss',
                                                    monthFormat: 'MMMM',
                                                    monthBeforeYear: true,
                                                    previousMonth: 'Önceki ay (PageUp)',
                                                    nextMonth: 'Sonraki ay (PageDown)',
                                                    previousYear: 'Önceki yıl (Control + left)',
                                                    nextYear: 'Sonraki yıl (Control + right)',
                                                    previousDecade: 'Önceki on yıl',
                                                    nextDecade: 'Sonraki on yıl',
                                                    previousCentury: 'Önceki yüzyıl',
                                                    nextCentury: 'Sonraki yüzyıl',
                                                },
                                                timePickerLocale: {
                                                    placeholder: 'Zaman seçin',
                                                },
                                            }}
                                        />
                                    </div>
                                </Col>
                            );
                        }
                    })()}

                    {/* Arama */}
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Arama
                            </Text>
                            <Input
                                placeholder="Arama yapın..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                prefix={<SearchOutlined style={{ color: darkMode ? 'var(--dark-text-muted)' : undefined }} />}
                                allowClear
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    </Col>
                    
                    {/* Vehicle Filter for Travel Tab */}
                    {activeTab === 'arac-yolculuklar-liste' && (
                        <Col xs={24} sm={12} md={6} lg={4}>
                            <div className="filter-item">
                                <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                    Araç
                                </Text>
                                <Select
                                    placeholder="Araç Seçin"
                                    value={vehicleFilter}
                                    onChange={(value) => setVehicleFilter(value)}
                                    style={{ width: '100%' }}
                                    className={darkMode ? 'dark-select' : ''}
                                    loading={vehicleOptions.length === 0}
                                >
                                    <Option value="ALL">Tümü</Option>
                                    {vehicleOptions.map(vehicle => (
                                        <Option key={vehicle.no} value={vehicle.no}>
                                            {vehicle.ad}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                    )}
                    
                    {/* Aksiyon Butonları */}
                    <Col xs={24} sm={24} md={6} lg={12}>
                        <div className="filter-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Space wrap>
                                {activeTab === 'araclar-liste' && (
                                    <>
                                        <Button
                                            key="yeni-arac-ekle"
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => {
                                                handleOpenForm(131, 'Araç Özellikleri', 'araclar', 'id', '');
                                            }}
                                            loading={loading}
                                            className={darkMode ? 'dark-button' : ''}
                                        >
                                            Yeni Araç
                                        </Button>
                                        <Button
                                            key="araclar-satilan"
                                            type="primary"
                                            icon={<ClockCircleOutlined />}
                                            onClick={() => navigate('/satilan_araclar?key_name=araclar-satilan')}
                                            loading={loading}
                                            className={darkMode ? 'dark-button' : ''}
                                        >
                                            Satılan Araçlar
                                        </Button>
                                    </>
                                )}
                                {activeTab === 'arac-sigorta-liste' && (
                                    <>
                                        <Button
                                            key="yeni-arac-ekle"
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => navigate('/satilan-araclar?key_name=tum_sigortalar')}
                                            loading={loading}
                                            className={darkMode ? 'dark-button' : ''}
                                        >
                                            Tüm Sigortalar
                                        </Button>
                                        <Button
                                            key="bitmis-sigorta"
                                            type="primary"
                                            icon={<ClockCircleOutlined />}
                                            onClick={() => navigate('/satilan-araclar?key_name=arac-bitmis_sigorta-liste')}
                                            loading={loading}
                                            className={darkMode ? 'dark-button' : ''}
                                        >
                                            Süresi Geçen Sigortalar
                                        </Button>
                                    </>
                                )}
                                {activeTab === 'arac-sozlesme-liste' && (
                                    <Button
                                        key="arac-sozlesme-liste"
                                        type="primary"
                                        onClick={() => handleOpenForm(146, 'Araç Sözleşmeleri', 'arac_sozlesme', 'id', '')}
                                        loading={loading}
                                        className={darkMode ? 'dark-button' : ''}
                                    >
                                        Sözleşme Ekle
                                    </Button>
                                )}
                                <Button
                                    onClick={clearAllFilters}
                                    className={darkMode ? 'dark-button' : ''}
                                    style={{
                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                        color: darkMode ? 'var(--dark-text)' : undefined
                                    }}
                                >
                                    Temizle
                                </Button>
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: 'excel',
                                                icon: <FileExcelOutlined />,
                                                label: 'Excel (.xlsx)',
                                                onClick: () => exportToExcel(filteredAndSortedData)
                                            },
                                            {
                                                key: 'print',
                                                icon: <PrinterOutlined />,
                                                label: 'Yazdır',
                                                onClick: () => printTable(filteredAndSortedData, { title: aracTabs.find(t => t.key === activeTab)?.label || 'Araçlar', message })
                                            }
                                        ]
                                    }}
                                    trigger={['click']}
                                >
                                    <Button
                                        icon={<DownloadOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Dışa Aktar
                                    </Button>
                                </Dropdown>
                                <Dropdown
                                    open={columnSelectDropdownOpen}
                                    onOpenChange={setColumnSelectDropdownOpen}
                                    trigger={['click']}
                                    dropdownRender={() => (
                                        <div style={{
                                            padding: '2px',
                                            minWidth: '250px',
                                            maxHeight: '400px',
                                            overflowY: 'auto',
                                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : '#fff',
                                            border: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}`,
                                            borderRadius: '6px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}>
                                            <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}` }}>
                                                <Checkbox
                                                    checked={selectedColumns.length === columns.length && columns.length > 0}
                                                    indeterminate={selectedColumns.length > 0 && selectedColumns.length < columns.length}
                                                    onChange={(e) => handleSelectAllColumns(e.target.checked)}
                                                    style={{ color: darkMode ? 'var(--dark-text)' : undefined }}
                                                >
                                                    <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                                        Tümünü Seç
                                                    </Text>
                                                </Checkbox>
                                            </div>
                                            <Checkbox.Group
                                                value={selectedColumns}
                                                onChange={handleColumnSelectionChange}
                                                style={{ width: '100%' }}
                                            >
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    {columns.map(column => (
                                                        <Checkbox
                                                            key={column.key}
                                                            value={column.key}
                                                            style={{
                                                                width: '100%',
                                                                color: darkMode ? 'var(--dark-text)' : undefined
                                                            }}
                                                        >
                                                            {column.title}
                                                        </Checkbox>
                                                    ))}
                                                </Space>
                                            </Checkbox.Group>
                                        </div>
                                    )}
                                >
                                    <Button
                                        icon={<EyeOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Sütun Seç
                                    </Button>
                                </Dropdown>
                                {/* Chatbot */}
                                <Chatbot />
                            </Space>
                        </div>
                    </Col>
                </Row>
            </Card>
            {/* Tablo */}
            <Card
                className={`fatura-content-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <div>
                    {/* Aksiyon Butonları */}
                    <div className="fatura-actions" style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Space>
                            <Button
                                icon={<BarChartOutlined />}
                                onClick={handlePivotRedirect}
                                disabled={loading || data.length === 0}
                                className={darkMode ? 'dark-button' : ''}
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                            >
                                Pivot Tabloya Git
                            </Button>
                        </Space>
                    </div>
                    {/* Tablo */}
                    {error && (
                        <Alert
                            message="Hata"
                            description={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}
                    <Table
                        dataSource={paginatedData}
                        loading={loading}
                        rowKey={(record) => record.key_banka_fatura_kes || record.id || record.No || Math.random()}
                        scroll={{ x: 'max-content' }}
                        className={darkMode ? 'dark-table' : ''}
                        rowClassName={(record) => {
                            if (activeTab === 'arac-sozlesme-liste') {
                                const bitisTarihiKey = Object.keys(record).find(key =>
                                    key.toLowerCase().includes('bitiş') || key.toLowerCase().includes('bitis')
                                );
                                if (bitisTarihiKey) {
                                    if (isDateExpired(bitisTarihiKey, record[bitisTarihiKey])) {
                                        return 'expired-row';
                                    }
                                }
                            }
                            if (activeTab === 'arac-yolculuklar-liste' && record._row_style) {
                                return record._row_style === 'red' ? 'weekend-row' : 'weekday-row';
                            }
                            return '';
                        }}
                        pagination={{
                            current: pagination.current,
                            total: filteredAndSortedData.length,
                            pageSize: pagination.pageSize,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} / ${total} kayıt`,
                            onChange: handlePageChange,
                            onShowSizeChange: handlePageChange,
                            pageSizeOptions: ['10', '25', '50', '100']
                        }}
                        size="small"
                        style={{
                            width: '100%',
                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                        }}
                        columns={visibleColumns.map((column, index) => {
                            const activeTabData = aracTabs.find(tab => tab.key === activeTab);
                            const hasActionButtons = activeTabData && activeTabData.actionButtons;
                            
                            // Sütun key'ine göre butonları bul - boşluk/alt çizgi farkını normalize et
                            const columnKey = column.key || '';
                            const normalizedKey = columnKey.replace(/\s+/g, '_').toUpperCase();
                            const actionButtonsForColumn = hasActionButtons ? (
                                activeTabData.actionButtons[columnKey] || 
                                activeTabData.actionButtons[normalizedKey] || 
                                activeTabData.actionButtons[columnKey.toLowerCase()] ||
                                activeTabData.actionButtons[columnKey.toUpperCase()] ||
                                []
                            ) : [];
                            const shouldShowActionButtonsForColumn = actionButtonsForColumn.length > 0;
                            
                            return {
                                ...column,
                                filters: undefined,
                                filterDropdown: false,
                                title: (
                                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <span style={{ flex: 1, cursor: 'pointer' }}>{column.title}</span>
                                        <Dropdown
                                            dropdownRender={() => getFilterDropdown(column)}
                                            trigger={['click']}
                                            placement="bottomRight"
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                style={{ 
                                                    marginLeft: 4,
                                                    color: filteredInfo[column.key] ? (darkMode ? 'var(--dark-text)' : '#1890ff') : '#ccc'
                                                }}
                                                className={darkMode ? 'dark-button' : ''}
                                            >
                                                <FilterOutlined style={{ fontSize: 12 }} />
                                            </Button>
                                        </Dropdown>
                                    </div>
                                ),
                                onHeaderCell: () => ({
                                    onContextMenu: (e) => {
                                        e.preventDefault();
                                        removeColumn(column.key);
                                    },
                                    onClick: () => {
                                        if (column.sortable) {
                                            handleSort(column.key);
                                        }
                                    }
                                }),
                                dataIndex: column.key,
                                key: column.key,
                                width: 50,
                                align: 'center',
                                ellipsis: { showTitle: true },
                                showSorterTooltip: false,
                                render: (text, record) => {
                                    const isDateColumn = column.key.toLowerCase().includes('tarih');
                                    const isBitisTarihi = column.key.toLowerCase().includes('bitiş') || column.key.toLowerCase().includes('bitis');
                                    let cellClassName = '';
                                    if (activeTab === 'arac-sozlesme-liste' && isBitisTarihi) {
                                        if (isDateExpired(column.key, text)) {
                                            cellClassName = 'expired-date';
                                        } else if (isDateApproaching(column.key, text)) {
                                            cellClassName = 'approaching-date';
                                        }
                                    }
                                    
                                    return (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {shouldShowActionButtonsForColumn ? (
                                                <ActionButtons
                                                    record={record}
                                                    actionButtons={actionButtonsForColumn}
                                                    darkMode={darkMode}
                                                    buttonLayout="vertical"
                                                />
                                            ) : (
                                                <span className={cellClassName}>
                                                    {formatCellValue(column.key, text)}
                                                </span>
                                            )}
                                        </div>
                                    );
                                }
                            };
                        })}
                    />
                </div>
            </Card>
            {/* Form Modal */}
            {formModalOpen && (
                <Modal
                    title={modalTitle}
                    open={formModalOpen}
                    onCancel={closeFormModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={800}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        <FormikForm
                            formId={currentFormId}
                            uniqueValue={currentUniqueValue}
                            uniqueField={formUniqueField}
                            tableName={formTableName}
                            extraData={currentFormExtraData}
                            onSuccess={handleFormSuccess}
                        />
                    </div>
                </Modal>
            )}
            {/* Detail Modal */}
            {detailModalOpen && (
                <Modal
                    title={modalTitle}
                    open={detailModalOpen}
                    onCancel={closeDetailModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={800}
                    style={{ top: 50 }}
                    className="demirbas-detail-modal"
                >
                    <div style={{ maxHeight: 400, overflowY: 'auto', fontSize: 12 }}>
                        {detailError && (
                            <Alert
                                message="Hata"
                                description={detailError}
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}
                        {modalButtonType === 'sub-table' && detailData.length > 0 && detailData[0].root ? (
                            <Table
                                dataSource={detailData}
                                loading={detailLoading}
                                rowKey={(record) => record.dosyaID || record.id || Math.random()}
                                scroll={{ x: 'max-content' }}
                                className={darkMode ? 'dark-table' : ''}
                                size="small"
                                style={{
                                    width: '100%',
                                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                                }}
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    showTotal: (total, range) =>
                                        `${range[0]}-${range[1]} / ${total} kayıt`,
                                    pageSizeOptions: ['10', '25', '50', '100']
                                }}
                            >
                                <Table.Column
                                    title="Plaka"
                                    dataIndex="Plaka"
                                    width={50}
                                    ellipsis={{ showTitle: true }}
                                />
                                <Table.Column
                                    title="Belge Tarihi"
                                    dataIndex="Belge Tarihi"
                                    width={50}
                                    ellipsis={{ showTitle: true }}
                                    render={(text) => {
                                        return formatCellValue('Belge Tarihi', text);
                                    }}
                                />
                                <Table.Column
                                    title="Açıklama"
                                    dataIndex="Açıklama"
                                    width={50}
                                    ellipsis={{ showTitle: true }}
                                />
                                <Table.Column
                                    title="İşlem"
                                    width={50}
                                    render={(text, record) => (
                                        <Space size="small">
                                            <Button
                                                type="default"
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={() => {
                                                    handleSubEditFile(record);
                                                }}
                                                className={darkMode ? 'dark-button' : ''}
                                                style={{
                                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                            >
                                                Düzenle
                                            </Button>
                                            <Button
                                                type="default"
                                                size="small"
                                                icon={<EyeOutlined />}
                                                onClick={() => handleViewFile(record)}
                                                className={darkMode ? 'dark-button' : ''}
                                                style={{
                                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                            >
                                                Dosyayı Aç
                                            </Button>
                                        </Space>
                                    )}
                                />
                            </Table>
                        ) : (
                            <Table
                                dataSource={detailData}
                                loading={detailLoading}
                                rowKey={(record) => record.dosyaID || record.id || Math.random()}
                                scroll={{ x: 'max-content' }}
                                className={darkMode ? 'dark-table' : ''}
                                size="small"
                                style={{
                                    width: '100%',
                                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                                }}
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    showTotal: (total, range) =>
                                        `${range[0]}-${range[1]} / ${total} kayıt`,
                                    pageSizeOptions: ['10', '25', '50', '100']
                                }}
                            >
                                {detailData && detailData.length > 0 && Object.keys(detailData[0]).map((key, index) => {
                                    const getColumnWidth = (key) => {
                                        const keyLength = key.length;
                                        if (key.toLowerCase().includes('tarih')) {
                                            return Math.max(120, keyLength * 8);
                                        }

                                        if (key.toLowerCase().includes('tutar') || key.toLowerCase().includes('fiyat') || key.toLowerCase().includes('ücret')) {
                                            return Math.max(120, keyLength * 8);
                                        }

                                        if (keyLength > 20) {
                                            return Math.max(200, keyLength * 8);
                                        }
                                        if (keyLength > 10) {
                                            return Math.max(120, keyLength * 8);
                                        }
                                        return Math.max(80, keyLength * 8);
                                    };
                                    const totalColumns = Object.keys(detailData[0]).length;
                                    const isLastColumn = index === totalColumns - 1;
                                    return (
                                        <Table.Column
                                            key={key}
                                            title={key}
                                            dataIndex={key}
                                            width={isLastColumn ? Math.max(getColumnWidth(key), 200) : getColumnWidth(key)}
                                            ellipsis={{
                                                showTitle: true
                                            }}
                                            render={(text, record) => (
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {isLastColumn ? (
                                                        <Space size="small">
                                                            {modalButtonType === 'main-table' ? (
                                                                <>
                                                                    <Button
                                                                        type="default"
                                                                        size="small"
                                                                        icon={<EditOutlined />}
                                                                        onClick={() => {
                                                                            handleEditFile(record);
                                                                        }}
                                                                        className={darkMode ? 'dark-button' : ''}
                                                                        style={{
                                                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                            color: darkMode ? 'var(--dark-text)' : undefined
                                                                        }}
                                                                    >
                                                                        Düzenle
                                                                    </Button>
                                                                    <Button
                                                                        type="default"
                                                                        size="small"
                                                                        icon={<PlusOutlined />}
                                                                        onClick={() => handleAddDelivery(record)}
                                                                        className={darkMode ? 'dark-button' : ''}
                                                                        style={{
                                                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                            color: darkMode ? 'var(--dark-text)' : undefined
                                                                        }}
                                                                    >
                                                                        Teslimat Ekle
                                                                    </Button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Button
                                                                        type="default"
                                                                        size="small"
                                                                        icon={<EyeOutlined />}
                                                                        onClick={() => handleViewFile(record)}
                                                                        className={darkMode ? 'dark-button' : ''}
                                                                        style={{
                                                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                            color: darkMode ? 'var(--dark-text)' : undefined
                                                                        }}
                                                                    >
                                                                        Görüntüle
                                                                    </Button>
                                                                    <Button
                                                                        type="default"
                                                                        size="small"
                                                                        icon={<EditOutlined />}
                                                                        onClick={() => {
                                                                            handleSubEditFile(record);
                                                                        }}
                                                                        className={darkMode ? 'dark-button' : ''}
                                                                        style={{
                                                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                            color: darkMode ? 'var(--dark-text)' : undefined
                                                                        }}
                                                                    >
                                                                        Düzenle
                                                                    </Button>
                                                                </>
                                                            )}
                                                        </Space>
                                                    ) : (
                                                        <span>{formatCellValue(key, text)}</span>
                                                    )}
                                                </div>
                                            )}
                                            sorter={(a, b) => {
                                                const aVal = a[key];
                                                const bVal = b[key];
                                                if (!isNaN(aVal) && !isNaN(bVal)) {
                                                    return Number(aVal) - Number(bVal);
                                                }
                                                return String(aVal).localeCompare(String(bVal), 'tr');
                                            }}
                                        />
                                    );
                                })}
                            </Table>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default AraclarPage;